// pages/Reminders/ReminderList.jsx
import { Box, Chip, IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Delete, MoreVert } from "@mui/icons-material";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { formatDate, updateSearchParams } from "../../utils/commonMethods";
import ModernServerTable from "../../components/ModernServerTable";
import ConfirmDialog from "../../components/ConfirmDialog";
import Avatar from "../../components/Avatar";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import {
  useGetRemindersQuery,
  useDeleteReminderMutation,
} from "../../store/services/remindersv2";

dayjs.extend(relativeTime);

const typeColor = {
  URGENT: { bg: "bg-red-50", text: "text-red-700", border: "border-red-200" },
  WARNING: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200" },
  INFO: { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200" },
};

const statusColor = {
  pending: { bg: "bg-yellow-50", text: "text-yellow-700", border: "border-yellow-200" },
  completed: { bg: "bg-green-50", text: "text-green-700", border: "border-green-200" },
  missed: { bg: "bg-red-50", text: "text-red-700", border: "border-red-200" },
};

const FILTERS = ["upcoming", "missed", "completed"];

const ReminderList = () => {
  const dispatch = useDispatch();
  const { auth } = useSelector((state) => state.auth);
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState("upcoming");
  // const [search, setSearch] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteId, setDeleteId] = useState(null); // single row delete
  const [paginationModel, setPaginationModel] = useState({
    page: parseInt(searchParams.get("page")) || 1,
    pageSize: 20,
  });

  const isAdmin = ["sup_admin", "sub_admin"].includes(auth?.role);

  const queryParams = useMemo(() => ({
    filter,
    page: paginationModel.page,
    limit: paginationModel.pageSize,
  }), [filter, paginationModel.page, paginationModel.pageSize]);

  const { data, isFetching } = useGetRemindersQuery(queryParams);
  // console.log("isFetching:", isFetching);

  // console.log(JSON.stringify(data, null, 2));

  const [deleteReminder, { isLoading: isDeleting }] = useDeleteReminderMutation();

  useEffect(() => {
    setPaginationModel((m) => ({
      ...m,
      page: parseInt(searchParams.get("page")) || 1,
    }));
  }, [searchParams.get("page")]);

  // useEffect(() => {
  //   const t = setTimeout(() => setSearch(searchValue), 500);
  //   return () => clearTimeout(t);
  // }, [searchValue]);

  const handleDeleteConfirm = async () => {
    try {
      const ids = deleteId ? [deleteId] : rowSelectionModel;
      await Promise.all(ids.map((id) => deleteReminder(id).unwrap()));
      toast.success("Reminder(s) deleted successfully");
      setRowSelectionModel([]);
      setDeleteId(null);
      setOpenDelete(false);
    } catch (err) {
      toast.error(err?.data?.message || "Failed to delete");
    }
  };

  const handleOpenBulkDelete = () => {
    if (!rowSelectionModel.length) {
      toast.warning("Please select at least 1 reminder");
      return;
    }
    setDeleteId(null);
    setOpenDelete(true);
  };

  const handleOpenRowDelete = (id) => {
    setDeleteId(id);
    setOpenDelete(true);
  };

  const columns = [
    {
      field: "title",
      headerName: "Title",
      minWidth: 180,
      flex: 1,
      renderCell: (params) => {
        const reminder = params?.row
        return (
          <div className="flex flex-col">
            <span className="text-sm">{reminder.title || "N/A"}</span>
            <span className="text-sm text-gray-400">{reminder.message || "N/A"}</span>
          </div>

        );
      },
    },
    {
      field: "leadId",
      headerName: "Lead",
      minWidth: 150,
      flex: 1,
      renderCell: (params) => {
        const lead = params?.row?.leadId;
        if (!lead) return <span className="text-gray-400 text-xs">No lead</span>;
        return (
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <Avatar name={lead.clientName} size="sm" showTooltip />
              <span className="text-sm">{lead.clientName || "N/A"}</span>
            </div>
          </div>

        );
      },
    },
    {
      field: "type",
      headerName: "Type",
      minWidth: 110,
      flex: 1,
      renderCell: (params) => {
        const t = params?.row?.type;
        const c = typeColor[t] || typeColor.INFO;
        return (
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs border ${c.bg} ${c.text} ${c.border}`}>
            {t}
          </span>
        );
      },
    },
    // {
    //   field: "status",
    //   headerName: "Status",
    //   minWidth: 120,
    //   flex: 1,
    //   renderCell: (params) => {
    //     const s = params?.row?.status;
    //     const c = statusColor[s] || statusColor.pending;
    //     return (
    //       <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs border capitalize ${c.bg} ${c.text} ${c.border}`}>
    //         {s}
    //       </span>
    //     );
    //   },
    // },
    {
      field: "reminderTime",
      headerName: "Reminder Time",
      minWidth: 180,
      flex: 1,
      renderCell: (params) => {
        const time = params?.row?.reminderTime;
        if (!time) return "N/A";
        const isPast = dayjs(time).isBefore(dayjs());
        return (
          <div className="flex flex-col">
            <span className="text-sm">{dayjs(time).format("DD MMM YYYY, hh:mm A")}</span>
            <span className={`text-xs ${isPast ? "text-red-400" : "text-gray-400"}`}>
              {dayjs(time).fromNow()}
            </span>
          </div>
        );
      },
    },
    // {
    //   field: "history",
    //   headerName: "Follow-ups",
    //   minWidth: 110,
    //   flex: 1,
    //   renderCell: (params) => {
    //     const count = params?.row?.history?.length || 0;
    //     return (
    //       <span className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-xs border ${count > 0
    //         ? "bg-purple-50 text-purple-700 border-purple-200"
    //         : "bg-gray-50 text-gray-500 border-gray-200"
    //         }`}>
    //         {count} {count === 1 ? "entry" : "entries"}
    //       </span>
    //     );
    //   },
    // },
    {
      field: "createdAt",
      headerName: "Created",
      minWidth: 150,
      flex: 1,
      valueGetter: (params) =>
        params?.row?.createdAt ? formatDate(params.row.createdAt) : "N/A",
    },
    {
      field: "action",
      headerName: "Action",
      sortable: false,
      minWidth: 80,
      flex: 1,
      renderCell: (params) => (
        <MoreMenu
          row={params.row}
          filter={filter}
          // onDelete={() => handleOpenRowDelete(params.row._id)}
          isAdmin={isAdmin}
        />
      ),
    },
  ];

  return (
    <Box>
      {/* filter tabs */}
      <Box sx={{ display: "flex", gap: 1, mb: 2, mt: 1 }}>
        {FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => {
              setFilter(f);
              updateSearchParams(setSearchParams, { page: 1 });
              setPaginationModel((m) => ({ ...m, page: 1 }));
            }}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium capitalize transition-all border ${filter === f
              ? "bg-gradient-to-r from-[#2e67be] to-[#4985f2] text-white border-transparent shadow"
              : "bg-white text-gray-600 border-gray-200 hover:border-[#4985f2] hover:text-[#4985f2]"
              }`}
          >
            {f}
            {filter === f && data?.pagination?.total > 0 && (
              <span className="ml-2 bg-white/20 text-white text-xs px-1.5 py-0.5 rounded-full">
                {data.pagination.total}
              </span>
            )}
          </button>
        ))}
      </Box>

      <ModernServerTable
        columns={columns}
        rows={data?.data}
        title={filter ? `${filter} Reminders` : "Reminders"}
        currentPage={paginationModel.page}
        pageSize={paginationModel.pageSize}
        totalItems={data?.pagination?.total}
        onPageChange={(page) => {
          updateSearchParams(setSearchParams, { page });
          setPaginationModel({ ...paginationModel, page });
        }}
        onPageSizeChange={(pageSize) =>
          setPaginationModel({ ...paginationModel, pageSize })
        }
        onSearchChange={(value) => setSearchValue(value)}
        isFetching={isFetching}
        isDelete={isAdmin}
        onDeleteSelected={handleOpenBulkDelete}
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={(newRows) => setRowSelectionModel(newRows)}
      />

      <ConfirmDialog
        open={openDelete}
        title="Are you sure you want to delete this reminder?"
        actionButtonLabel="Delete"
        handleConfirm={handleDeleteConfirm}
        // loading={isDeleting}
        handleClose={() => {
          setOpenDelete(false);
          setDeleteId(null);
        }}
      />
    </Box>
  );
};

const MoreMenu = ({ row, filter, onDelete, isAdmin }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  return (
    <>
      <MoreVert sx={{ cursor: "pointer" }} onClick={(e) => setAnchorEl(e.currentTarget)} />
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        MenuListProps={{ "aria-labelledby": "reminder-action-menu" }}
      >
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            if (row.leadId?._id) window.open(`/leads/${row.leadId._id}`, "_blank");
          }}
          disabled={!row.leadId?._id}
        >
          Open Lead
        </MenuItem>

        {/* {row.history?.length > 0 && (
          <MenuItem onClick={() => setAnchorEl(null)}>
            View History ({row.history.length})
          </MenuItem>
        )} */}

        {/* {filter === "upcoming" && (
          <MenuItem
            onClick={() => { setAnchorEl(null); onDelete(); }}
            sx={{ color: "error.main" }}
          >
            Cancel
          </MenuItem>
        )} */}
      </Menu>
    </>
  );
};

export default ReminderList;